import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import jwtDecode from "jwt-decode";
import { FiArrowLeft, FiCheckCircle, FiRefreshCw } from "react-icons/fi";
import { openOtpModal, closeOtpModal } from "../../features/withdraw-otp/withdrawOtpSlice";
import { openModal as open2FAModal } from "../../features/two-factor-modal/twoFactorModalSlice";
import { selectCurrentToken, selectCurrentUser } from "../../features/auth/authSlice";
import { WithdrawOtpModal } from "../../components/Dashboard/withdraw-now/WithdrawOtpModal";
import { useGetWalletQuery } from "../../features/wallet/walletSlice";
import {
  useGetMomoConfigQuery,
  useResolveMomoMutation,
  useGetMomoQuoteMutation,
  useCreateMomoWithdrawalMutation,
  useCreateMomoWithdrawalWithOtpMutation,
  useListMyMomoWithdrawalsQuery,
  MomoResolveResponse,
  MomoQuoteResponse,
  CreateMomoWithdrawalBody,
} from "../../features/momo/momoSlice";
import {
  MOMO_STATUS_LABEL,
  MOMO_STATUS_CLASS,
  fmtNaira,
  fmtCedi,
  fmtMsisdn,
} from "../../features/momo/status";
import { WITHDRAWAL_SOURCES, SOURCE_LABELS, WithdrawalSource, balanceFor, normalizeSource } from "../../features/withdraws/source";

// Ghana numbers: 0XXXXXXXXX, 233XXXXXXXXX or +233XXXXXXXXX. The backend
// normalises; this only decides when "Verify number" is worth a provider call.
const GH_PHONE = /^(\+?233\d{9}|0\d{9})$/;

const QUOTE_DEBOUNCE_MS = 500;

export default function MomoWithdraw() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);

  const { data: config, isLoading: configLoading, isError: configError } = useGetMomoConfigQuery();
  const { data: wallet } = useGetWalletQuery(undefined as any);
  const { data: history, refetch: refetchHistory } = useListMyMomoWithdrawalsQuery({ page: 1, limit: 10 });

  const [resolveMomo, { isLoading: resolving }] = useResolveMomoMutation();
  const [getQuote, { isLoading: quoting }] = useGetMomoQuoteMutation();
  const [createWithdrawal, { isLoading: creating }] = useCreateMomoWithdrawalMutation();
  const [createWithOtp] = useCreateMomoWithdrawalWithOtpMutation();

  // Which balance pays (Main / Reward / Referral) — same choice as the bank
  // withdraw form; prefilled from ?source= when the user came from there.
  const [searchParams] = useSearchParams();
  const [source, setSource] = useState<WithdrawalSource>(normalizeSource(searchParams.get("source")));
  const [networkId, setNetworkId] = useState("");
  const [phone, setPhone] = useState("");
  const [resolved, setResolved] = useState<MomoResolveResponse | null>(null);
  const [amountNgn, setAmountNgn] = useState("");
  const [quote, setQuote] = useState<MomoQuoteResponse | null>(null);
  const [step, setStep] = useState<"form" | "review">("form");
  const [pendingBody, setPendingBody] = useState<CreateMomoWithdrawalBody | null>(null);
  const quoteTimer = useRef<number | null>(null);

  const enabled = config?.enabled === true;
  const networks = enabled ? config.networks : [];

  useEffect(() => {
    if (enabled && !networkId && networks.length > 0) setNetworkId(networks[0].id);
  }, [enabled, networks, networkId]);

  // Any change to the recipient invalidates the resolved name.
  useEffect(() => {
    setResolved(null);
  }, [networkId, phone]);

  const amountNum = Number(amountNgn);
  const phoneValid = GH_PHONE.test(phone.replace(/\s+/g, ""));

  // Live GH₵ quote, debounced — each quote reads the admin rate on the server.
  useEffect(() => {
    if (quoteTimer.current) window.clearTimeout(quoteTimer.current);
    if (!enabled || !Number.isFinite(amountNum) || amountNum <= 0) {
      setQuote(null);
      return;
    }
    quoteTimer.current = window.setTimeout(async () => {
      try {
        const q = await getQuote({ amountNgn: amountNum }).unwrap();
        setQuote(q);
      } catch {
        setQuote(null);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      if (quoteTimer.current) window.clearTimeout(quoteTimer.current);
    };
  }, [amountNum, enabled, getQuote]);

  const amountError = useMemo(() => {
    if (!enabled || amountNgn === "") return null;
    if (!Number.isFinite(amountNum) || amountNum <= 0) return "Enter an amount.";
    if (amountNum < config.minNgn) return `Minimum is ${fmtNaira(config.minNgn)}.`;
    const available = balanceFor(wallet, source);
    if (available != null && amountNum > available) return `That is more than your available ${SOURCE_LABELS[source].toLowerCase()} balance.`;
    if (quote) {
      if (quote.payoutAmountGhs < quote.minGhs) return `Minimum payout is ${fmtCedi(quote.minGhs)}.`;
      if (quote.payoutAmountGhs > quote.maxGhs) return `Maximum payout is ${fmtCedi(quote.maxGhs)}.`;
    }
    return null;
  }, [enabled, config, amountNgn, amountNum, wallet, quote, source]);

  const handleResolve = async () => {
    if (!phoneValid || !networkId) return;
    try {
      const r = await resolveMomo({ networkId, phone: phone.replace(/\s+/g, "") }).unwrap();
      setResolved(r);
    } catch (err: any) {
      toast.error(err?.data?.message || "We couldn't find an account for that number.");
    }
  };

  const canReview = !!resolved && !!quote && !amountError && !quoting;

  const buildBody = (): CreateMomoWithdrawalBody => ({
    networkId,
    phone: resolved?.msisdn || phone.replace(/\s+/g, ""),
    amountNgn: amountNum,
    expectedPayoutGhs: quote?.payoutAmountGhs,
    source,
  });

  const resetAll = () => {
    setPhone("");
    setResolved(null);
    setAmountNgn("");
    setQuote(null);
    setPendingBody(null);
    setStep("form");
  };

  const onSuccess = () => {
    toast.success("Mobile money withdrawal submitted. Track it below.");
    resetAll();
    refetchHistory();
  };

  const showError = (err: any, fallback: string) => {
    const msg = err?.data?.message;
    if (typeof msg === "string") toast.error(msg);
    else if (Array.isArray(msg)) msg.forEach((m: string) => toast.error(m));
    else toast.error(fallback);
  };

  const handleConfirm = async () => {
    if (!canReview) return;

    // Same 2FA gate as the fiat withdraw page: the backend requires a 2FA-
    // authenticated token when the user has 2FA on.
    try {
      const decoded: { exp: number; is2FAAuthenticated?: boolean } = jwtDecode(token!);
      if (user?.is2FAEnabled && (!decoded.is2FAAuthenticated || decoded.exp < Date.now() / 1000)) {
        dispatch(open2FAModal());
        return;
      }
    } catch {
      // unreadable token: let the backend decide
    }

    const body = buildBody();
    try {
      await createWithdrawal(body).unwrap();
      onSuccess();
    } catch (err: any) {
      if (err?.status === 401 && err?.data?.message?.includes("OTP required") && err?.data?.challengeId) {
        setPendingBody(body);
        dispatch(
          openOtpModal({
            challengeId: err.data.challengeId,
            ttlSeconds: err.data.instructions?.ttlSeconds || 300,
          })
        );
        toast.error("OTP verification required. Check your email.");
      } else if (err?.status === 409) {
        // Rate moved since the quote — refresh and ask again.
        showError(err, "The rate changed. Please review the new amount.");
        setStep("form");
        setQuote(null);
        setAmountNgn(a => a);
      } else {
        showError(err, "Could not submit the withdrawal.");
      }
    }
  };

  const handleOtpSubmit = async (challengeId: string, otpCode: string) => {
    if (!pendingBody) return;
    try {
      await createWithOtp({ body: pendingBody, challengeId, otpCode }).unwrap();
      dispatch(closeOtpModal());
      onSuccess();
    } catch (err: any) {
      showError(err, "Invalid or expired OTP.");
      throw err;
    }
  };

  const network = networks.find(n => n.id === networkId);

  return (
    <div className="container-lg font-dashboard py-6 text-white">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white mb-4">
        <FiArrowLeft /> Back
      </button>

      <h1 className="text-2xl font-semibold mb-1">Mobile Money (Ghana)</h1>
      <p className="text-white/60 text-sm mb-6">
        Withdraw your Naira balance to a Ghana mobile money wallet in cedis.
        {balanceFor(wallet, source) != null && (
          <>
            {" "}Available {SOURCE_LABELS[source].toLowerCase()} balance: <span className="text-white">{fmtNaira(balanceFor(wallet, source)!)}</span>.
          </>
        )}
      </p>

      {configLoading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : configError || !config ? (
        <p className="text-red-400 text-sm">Could not load mobile money settings. Please try again later.</p>
      ) : !enabled ? (
        <div className="bg-black rounded-xl p-5 border border-primary/20 max-w-lg">
          <p className="text-white/80">Mobile money withdrawals are not available right now.</p>
          <Link to="/app/withdraw" className="btn-primary mt-4 inline-block px-4 py-2 rounded-md">
            Other withdrawal options
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* ── Left: form / review ── */}
          <div className="bg-black rounded-xl p-5 border border-primary/20">
            {step === "form" ? (
              <>
                <label className="text-sm text-white/70">Pay from</label>
                <div className="grid grid-cols-3 gap-2 mt-1 mb-4">
                  {WITHDRAWAL_SOURCES.map(src => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setSource(src)}
                      className={`form-input text-sm ${source === src ? "border-primary text-white" : "text-white/60"}`}
                    >
                      {SOURCE_LABELS[src]}
                      {balanceFor(wallet, src) != null && (
                        <span className="block text-[11px] text-white/40">{fmtNaira(balanceFor(wallet, src)!)}</span>
                      )}
                    </button>
                  ))}
                </div>

                <label className="text-sm text-white/70">Network</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {networks.map(n => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setNetworkId(n.id)}
                      className={`form-input text-sm ${networkId === n.id ? "border-primary text-white" : "text-white/60"}`}
                    >
                      {n.name}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <label htmlFor="momo-phone" className="text-sm text-white/70">Mobile money number</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      id="momo-phone"
                      type="tel"
                      inputMode="tel"
                      placeholder="024 123 4567"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className="form-input flex-1"
                    />
                    <button
                      type="button"
                      onClick={handleResolve}
                      disabled={!phoneValid || !networkId || resolving}
                      className="btn-secondary px-4 whitespace-nowrap disabled:opacity-50"
                    >
                      {resolving ? "Checking…" : resolved ? "Re-check" : "Verify number"}
                    </button>
                  </div>
                  {phone !== "" && !phoneValid && (
                    <p className="text-red-500 text-xs mt-1">Enter a Ghana number, e.g. 024 123 4567 or +233 24 123 4567.</p>
                  )}
                  {resolved && (
                    <p className="flex items-center gap-2 text-green-400 text-sm mt-2">
                      <FiCheckCircle /> {resolved.accountName}
                      <span className="text-white/50">· {resolved.network.name} {fmtMsisdn(resolved.msisdn)}</span>
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <label htmlFor="momo-amount" className="text-sm text-white/70">Amount (₦)</label>
                  <input
                    id="momo-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={`From ${fmtNaira(config.minNgn)}`}
                    value={amountNgn}
                    onWheel={e => e.currentTarget.blur()}
                    onChange={e => setAmountNgn(e.target.value)}
                    className="form-input spin-button-none mt-1"
                  />
                  {amountError ? (
                    <p className="text-red-500 text-xs mt-1">{amountError}</p>
                  ) : quote ? (
                    <p className="text-white/70 text-sm mt-2">
                      Recipient gets <span className="text-white font-semibold">{fmtCedi(quote.payoutAmountGhs)}</span>
                      <span className="text-white/40"> · ₦{quote.fxRate.toLocaleString()} per GH₵1</span>
                    </p>
                  ) : amountNgn !== "" && quoting ? (
                    <p className="text-white/40 text-xs mt-1">Getting rate…</p>
                  ) : (
                    <p className="text-white/40 text-xs mt-1">
                      Payouts between {fmtCedi(config.minGhs)} and {fmtCedi(config.maxGhs)}.
                    </p>
                  )}
                </div>

                <button
                  onClick={() => setStep("review")}
                  disabled={!canReview}
                  className="btn-primary w-full mt-6 py-4 text-base font-medium disabled:opacity-50"
                >
                  Review
                </button>
                {!resolved && phoneValid && (
                  <p className="text-white/40 text-xs mt-2 text-center">Verify the number to continue.</p>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-medium">Review Withdrawal</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <Row label="Network" value={network?.name ?? resolved?.network.name} />
                  <Row label="Number" value={fmtMsisdn(resolved?.msisdn || phone)} />
                  <Row label="Account name" value={resolved?.accountName} />
                  <Row label="Paid from" value={SOURCE_LABELS[source]} />
                  <Row label="You withdraw" value={fmtNaira(amountNum)} />
                  <Row label="Rate" value={`₦${quote?.fxRate.toLocaleString()} per GH₵1`} />
                  <div className="border-t border-white/10 pt-3">
                    <Row
                      label={<span className="text-white">Recipient gets</span>}
                      value={<span className="text-white font-semibold">{quote ? fmtCedi(quote.payoutAmountGhs) : "—"}</span>}
                    />
                  </div>
                </dl>
                <p className="text-white/40 text-xs mt-4">
                  Your Naira balance is debited now. If the payout fails, it is credited back automatically.
                </p>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep("form")} className="form-input flex-1 py-4 text-white/80">
                    Edit
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={creating || !canReview}
                    className="btn-primary flex-1 py-4 text-base font-medium disabled:opacity-50"
                  >
                    {creating ? "Submitting…" : "Confirm"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── Right: recent withdrawals ── */}
          <div className="bg-black rounded-xl p-5 border border-primary/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Recent Mobile Money Withdrawals</h2>
              <button onClick={() => refetchHistory()} className="text-white/50 hover:text-white" title="Refresh">
                <FiRefreshCw />
              </button>
            </div>
            {!history || history.data.length === 0 ? (
              <p className="text-white/50 text-sm">No mobile money withdrawals yet.</p>
            ) : (
              <ul className="space-y-3">
                {history.data.map(w => (
                  <li key={w._id} className="flex items-start justify-between gap-3 border-b border-white/10 pb-3 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">
                        {w.network?.name} {fmtMsisdn(w.msisdn)}
                      </p>
                      <p className="text-xs text-white/50 truncate">
                        {w.accountName}
                        {w.source && w.source !== "account" && <> · from {SOURCE_LABELS[w.source]}</>}
                      </p>
                      <p className="text-xs text-white/40 mt-1">{new Date(w.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm text-white font-medium">{fmtCedi(w.payoutAmountGhs)}</p>
                      <p className="text-xs text-white/50">{fmtNaira(w.amountNgn)}</p>
                      <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded ${MOMO_STATUS_CLASS[w.status] ?? "bg-gray-500/15 text-gray-300"}`}>
                        {MOMO_STATUS_LABEL[w.status] ?? w.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <WithdrawOtpModal onOtpSubmit={handleOtpSubmit} />
    </div>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-white/60">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
